const DEFAULT_IFRAME_TITLE = 'Embedded content';

const EVENT_HANDLER_ATTRIBUTE_PREFIX = 'on';
const ALLOWED_IFRAME_ATTRIBUTES = new Set(['src', 'title', 'width', 'height']);

const normalize_origin_candidate = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const url = new URL(trimmed);
    if (url.protocol !== 'https:') return null;
    return url.origin;
  } catch {
    return null;
  }
};

export const parse_trusted_iframe_origins = (value: string | undefined): Set<string> => {
  if (!value) return new Set();

  return new Set(
    value
      .split(',')
      .map(entry => normalize_origin_candidate(entry))
      .filter((entry): entry is string => Boolean(entry))
  );
};

export const trusted_iframe_origins = parse_trusted_iframe_origins(
  import.meta.env.VITE_BLOG_TRUSTED_IFRAME_ORIGINS
);

export const is_trusted_iframe_src = (
  src: string,
  origins: Set<string> = trusted_iframe_origins
): boolean => {
  try {
    const url = new URL(src.trim());
    return url.protocol === 'https:' && origins.has(url.origin);
  } catch {
    return false;
  }
};

// Known embed-only path prefixes for the providers this allowlist is meant for. A URL whose
// origin is trusted but whose path isn't one of these (e.g. a YouTube "watch" page rather than
// an "embed" page) can't actually be framed by the provider and would only produce a broken
// embed, so it's treated as non-embeddable even though the origin itself is trusted.
const EMBEDDABLE_PATH_PREFIXES_BY_ORIGIN: Record<string, string> = {
  'https://www.youtube.com': '/embed/',
  'https://www.youtube-nocookie.com': '/embed/',
  'https://player.vimeo.com': '/video/',
};

export const is_embeddable_iframe_src = (
  src: string,
  origins: Set<string> = trusted_iframe_origins
): boolean => {
  if (!is_trusted_iframe_src(src, origins)) return false;

  const url = new URL(src.trim());
  const requiredPrefix = EMBEDDABLE_PATH_PREFIXES_BY_ORIGIN[url.origin];
  // Origins without a known embed-path convention (e.g. a custom origin configured via env)
  // are left to the origin allowlist alone; we only special-case the providers we know about.
  if (!requiredPrefix) return true;

  return url.pathname.startsWith(requiredPrefix);
};

const remove_event_handler_attributes = (element: Element) => {
  for (const attribute of Array.from(element.attributes)) {
    if (attribute.name.toLowerCase().startsWith(EVENT_HANDLER_ATTRIBUTE_PREFIX)) {
      element.removeAttribute(attribute.name);
    }
  }
};

export const sanitize_blog_iframe_html = (
  html: string,
  origins: Set<string> = trusted_iframe_origins
): string => {
  if (!/<iframe/i.test(html)) return html;

  const template = document.createElement('template');
  template.innerHTML = html;

  const iframes = Array.from(template.content.querySelectorAll('iframe'));
  for (const iframe of iframes) {
    const src = iframe.getAttribute('src')?.trim() ?? '';

    remove_event_handler_attributes(iframe);

    if (!src || iframe.hasAttribute('srcdoc') || !is_trusted_iframe_src(src, origins)) {
      iframe.remove();
      continue;
    }

    for (const attribute of Array.from(iframe.attributes)) {
      if (!ALLOWED_IFRAME_ATTRIBUTES.has(attribute.name.toLowerCase())) {
        iframe.removeAttribute(attribute.name);
      }
    }

    iframe.setAttribute('src', src);
    iframe.setAttribute('title', iframe.getAttribute('title') || DEFAULT_IFRAME_TITLE);
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.setAttribute(
      'sandbox',
      'allow-scripts allow-same-origin allow-popups allow-forms allow-presentation'
    );
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.setAttribute('allowfullscreen', '');
    if (!iframe.getAttribute('width')) {
      iframe.setAttribute('width', '100%');
    }
  }

  return template.innerHTML;
};

export const extract_iframe_embed_from_html = (
  html: string
): { src: string; title: string | null } | null => {
  if (!/<iframe/i.test(html)) return null;
  const template = document.createElement('template');
  template.innerHTML = html;
  const iframe = template.content.querySelector('iframe[src]');
  const src = iframe?.getAttribute('src')?.trim();
  if (!src) return null;
  return { src, title: iframe?.getAttribute('title')?.trim() || null };
};

export type PastedIframeEmbedDecision =
  | { action: 'insert'; src: string; title: string | null }
  | { action: 'block'; reason: 'untrusted_origin' | 'not_embeddable' }
  | { action: 'ignore' };

/**
 * Given a clipboard payload, decides whether it contains embeddable iframe markup and, if so,
 * whether it should be inserted or blocked. Providers' "copy embed code" buttons sometimes put
 * the markup in text/html and sometimes in text/plain, so both are checked; a bare URL with no
 * `<iframe>` markup at all is left alone ("ignore") so it falls through to normal link handling.
 *
 * When blocking, the reason distinguishes an origin that isn't allowlisted at all from an
 * allowlisted origin whose path isn't an actual embed path (e.g. a YouTube "watch" URL), since
 * those two cases need different user-facing messages.
 */
export const resolve_pasted_iframe_embed = (
  clipboardData: Pick<DataTransfer, 'getData'> | null | undefined,
  origins: Set<string> = trusted_iframe_origins
): PastedIframeEmbedDecision => {
  if (!clipboardData) return { action: 'ignore' };

  const html = clipboardData.getData('text/html');
  const plainText = clipboardData.getData('text/plain').trim();
  const embed = extract_iframe_embed_from_html(html) ?? extract_iframe_embed_from_html(plainText);

  if (!embed) return { action: 'ignore' };
  if (!is_embeddable_iframe_src(embed.src, origins)) {
    return {
      action: 'block',
      reason: is_trusted_iframe_src(embed.src, origins) ? 'not_embeddable' : 'untrusted_origin',
    };
  }

  return { action: 'insert', src: embed.src, title: embed.title };
};
